// Medical Portal — "My Doctors" page
//
// Data source: our own Mongo `RefDoctor` collection, kept in sync with
// IT Dose's doctor_referal table by the backend. The flow on the
// server is:
//
//   GET /api/portal/ref-doctors?…
//     ├─ if centre's cache is stale (>5 min) → sync from IT Dose
//     │  (upsert by itdose_doctor_id, soft-remove deletions)
//     └─ query Mongo with the requested filter/sort/page → return
//
//   GET /api/portal/ref-doctors/specializations
//     → distinct specializations for this centre (powers the dropdown)
//
// That keeps three properties simultaneously true:
//   • The list is always fresh (auto-syncs on open).
//   • Pagination / search / sort clicks are snappy (Mongo only).
//   • The page works when IT Dose is unreachable (cached data shown).
//
// The Refresh button passes force=1 to bypass the 5-min cache and
// pull from IT Dose immediately.

import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../../context/AuthContext';
import { MP, MONO, DISPLAY } from './shared';
import {
  useListRefDoctorsQuery,
  useListRefDoctorSpecializationsQuery,
} from '../../../store/refDoctorsApi';

const PAGE_SIZE = 20;

const initialsOf = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const skipFirst = /^(dr|mr|mrs|ms|mx|prof)\.?$/i.test(parts[0]) && parts.length > 1;
  const picks = skipFirst ? parts.slice(1) : parts;
  return picks.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';
};

const MyDoctors = () => {
  const { user } = useAuth();
  const hasCentre = !!user?.client_id;

  // ─── List state ────────────────────────────────────────────
  const [page, setPage]                   = useState(1);
  const [search, setSearch]               = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort]                   = useState('name');
  const [specialization, setSpecialization] = useState('');
  // `forceKey` bumps the cache-busting flag for one refetch. RTK Query
  // keys its cache by the params object, so passing a fresh `force`
  // value invalidates the cached page and triggers a re-sync.
  const [forceKey, setForceKey]           = useState(0);

  // Debounce search — keeps the cursor responsive while we don't
  // hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever filters change — otherwise a stale page
  // index can end up past the new last page.
  useEffect(() => { setPage(1); }, [debouncedSearch, sort, specialization]);

  const queryArgs = {
    page,
    limit: PAGE_SIZE,
    q: debouncedSearch,
    sort,
    specialization,
    // Pass force only when the user clicks Refresh — every other
    // re-render keeps it false so we hit the cached Mongo state.
    force: forceKey > 0 && page === 1 && !debouncedSearch && !specialization,
  };

  const {
    data, isLoading, isFetching, error, refetch,
  } = useListRefDoctorsQuery(queryArgs);
  // Specialization dropdown is itself dynamic — backend returns the
  // distinct set of specs present for THIS centre, so we never show
  // a stale or hardcoded option. Auto-refetches when the list cache
  // is invalidated (e.g. after a force-refresh).
  const { data: specsData } = useListRefDoctorSpecializationsQuery();

  const items      = data?.items || [];
  const pagination = data?.pagination || { total: 0, page: 1, pages: 1 };
  const specOptions = useMemo(() => specsData?.items || [], [specsData]);

  const onRefresh = () => {
    setForceKey((k) => k + 1);
    refetch();
  };

  if (!hasCentre) {
    return (
      <Alert severity="warning" sx={{ fontFamily: MONO }}>
        No centre is associated with your account.
      </Alert>
    );
  }

  const showSkeleton = isLoading || (isFetching && items.length === 0);

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: MP.dark }}>
            My Doctors
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 13, color: MP.textMuted }}>
            {isLoading ? 'Loading…'
              : `${pagination.total} ${pagination.total === 1 ? 'doctor' : 'doctors'} referring to your centre`}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search name, specialization, hospital, mobile, code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { md: 300 }, '& .MuiInputBase-root': { fontFamily: MONO, bgcolor: MP.white } }}
        />
        <TextField
          size="small"
          select
          value={specialization}
          onChange={(e) => setSpecialization(e.target.value)}
          sx={{ minWidth: 180, '& .MuiInputBase-root': { fontFamily: MONO, bgcolor: MP.white } }}
        >
          <MenuItem value="">All specializations</MenuItem>
          {specOptions.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          sx={{ minWidth: 170, '& .MuiInputBase-root': { fontFamily: MONO, bgcolor: MP.white } }}
        >
          <MenuItem value="name">Name (A–Z)</MenuItem>
          <MenuItem value="specialization">Specialization</MenuItem>
          <MenuItem value="code">Doctor code</MenuItem>
          <MenuItem value="recent">Recently synced</MenuItem>
        </TextField>
        <IconButton
          size="small"
          onClick={onRefresh}
          disabled={isFetching}
          title="Refresh from IT Dose"
          sx={{ bgcolor: MP.tealLight, color: MP.tealDark, '&:hover': { bgcolor: MP.tealMid } }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontFamily: MONO }}>
          {error?.data?.message || 'Could not load doctors.'}
        </Alert>
      )}

      {/* Table card */}
      <Box sx={{ bgcolor: MP.white, border: `1px solid ${MP.border}`, borderRadius: 2, overflow: 'hidden' }}>
        <Grid container sx={{ px: '11px', pt: '11px', pb: '9px', bgcolor: MP.white, boxShadow: `inset 0 -1px 0 ${MP.lnBot}`, fontFamily: MONO, fontSize: 11, fontWeight: 700, color: MP.dark, letterSpacing: '.02em', textTransform: 'uppercase' }}>
          <Grid item xs={12} md={3}>Doctor</Grid>
          <Grid item md={2.5} sx={{ display: { xs: 'none', md: 'block' } }}>Specialization</Grid>
          <Grid item md={2} sx={{ display: { xs: 'none', md: 'block' } }}>Mobile</Grid>
          <Grid item md={2.5} sx={{ display: { xs: 'none', md: 'block' } }}>Location</Grid>
          <Grid item md={2} sx={{ display: { xs: 'none', md: 'block' } }}>Code</Grid>
        </Grid>

        {showSkeleton ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Grid container key={i} sx={{ px: '11px', py: '9px', borderBottom: `1px solid ${MP.borderSoft}` }}>
              <Grid item xs={12}><Skeleton variant="text" height={28} /></Grid>
            </Grid>
          ))
        ) : items.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 6, gap: 1.5 }}>
            <PersonOutlineIcon sx={{ fontSize: 48, color: MP.tealMid }} />
            <Typography sx={{ fontFamily: MONO, fontSize: 14, color: MP.textMuted }}>
              {debouncedSearch || specialization
                ? 'No doctors match your filters.'
                : 'No doctors are mapped to your centre yet.'}
            </Typography>
          </Stack>
        ) : (
          items.map((d) => (
            <Grid container key={d._id} alignItems="center" sx={{ px: '11px', py: '9px', borderBottom: `1px solid ${MP.borderSoft}`, '&:hover': { bgcolor: MP.rowHover } }}>
              <Grid item xs={12} md={3}>
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: MP.tealLight, color: MP.tealDark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: DISPLAY, fontWeight: 700 }}>
                    {initialsOf(d.name)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 600, color: MP.text, lineHeight: 1.3 }} noWrap>
                      {d.name}
                    </Typography>
                    {d.email && (
                      <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }} noWrap>
                        {d.email}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Grid>
              <Grid item md={2.5} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 13, color: MP.text }} noWrap>
                  {d.specialization || '—'}
                </Typography>
                {d.hospital && (
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }} noWrap>
                    {d.hospital}
                  </Typography>
                )}
              </Grid>
              <Grid item md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 13, color: MP.text }}>{d.mobile || '—'}</Typography>
                {d.landline && (
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>{d.landline}</Typography>
                )}
              </Grid>
              <Grid item md={2.5} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 13, color: MP.text }} noWrap>
                  {[d.locality, d.city].filter(Boolean).join(', ') || '—'}
                </Typography>
                {d.state && (
                  <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }} noWrap>
                    {d.state}
                  </Typography>
                )}
              </Grid>
              <Grid item md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: MP.tealDark }}>
                  {d.doctor_code || '—'}
                </Typography>
              </Grid>
            </Grid>
          ))
        )}

        {/* Pagination — server-side, Prev/Next + page indicator */}
        {!showSkeleton && pagination.pages > 1 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, bgcolor: MP.bg }}>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MP.textMuted }}>
              Page {pagination.page} of {pagination.pages} · {pagination.total} {pagination.total === 1 ? 'result' : 'results'}
            </Typography>
            <Stack direction="row" gap={0.5}>
              <IconButton
                size="small"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                size="small"
                disabled={page >= pagination.pages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MyDoctors;
